import type { HtsAdminFollowUpStatus, HtsGroupField, HtsInputType, HtsSubmissionStatus } from '@evaltick/shared';
// Value import (not `import type`) — the interfaces below reference
// `typeof HtsSubmissionSummaryStatus.Ready` for the always-ready summary.
import { HtsSubmissionSummaryStatus } from '@evaltick/shared';
import { BaseAPIClient } from './base';

// =============================================================================
// TYPES
// =============================================================================

export interface HtsQuestion {
  id: string;
  interviewTemplateId: string;
  section: string;
  order: number;
  inputType: HtsInputType;
  text: string;
  helpText: string | null;
  options: string[];
  /** Sub-field definitions for inputType REPEATABLE_GROUP — null for every other inputType. */
  groupFields: HtsGroupField[] | null;
}

/** One "readiness" pillar's candidate-safe label — see HtsReadinessSummary. */
export interface HtsReadinessSummary {
  status:
    | typeof HtsSubmissionSummaryStatus.Ready
    | typeof HtsSubmissionSummaryStatus.PendingReview
    | typeof HtsSubmissionSummaryStatus.WithheldFromCandidate;
  bandLabel?: string;
  provisional?: boolean;
  strengths?: string[];
  priorities?: string[];
  missingPillarLabels?: string[];
  /** Numbers-free coaching narrative — absent if the drafting call failed; the tag lists above still render either way. */
  narrative?: { strengths: string; priorities: string; bottomLine: string };
}

/** One of the current user's own HTS submissions — for the session-history list. */
export interface HtsSubmissionListItemForUser {
  id: string;
  interviewTemplateId: string;
  interviewTemplateName: string;
  /** Non-null once an admin revoked the template — hides "Try again". */
  interviewTemplateRevokedAt: string | null;
  interviewTemplateQuestionCount: number;
  submittedAt: string | null;
  /** Sort/display fallback for drafts, whose submittedAt is null. */
  updatedAt: string;
  /** Draft = not yet submitted, resumable. Ready = submitted. */
  status: HtsSubmissionSummaryStatus;
  bandLabel: string | null;
  /** The admin follow-up round's status (NotSent for non-guests). Carries no question text. */
  adminFollowUpStatus: HtsAdminFollowUpStatus;
  /** When a guest can no longer log back in (past or future); null for non-guests. */
  guestLoginEndsAt: string | null;
}

export interface PaginatedHtsSubmissionsForUser {
  data: HtsSubmissionListItemForUser[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface HtsQuestionsForTemplate {
  interviewTemplateId: string;
  questions: HtsQuestion[];
  // The user's own submission (or null if they haven't started one), folded in
  // so the page can tell new-vs-resume without creating a submission.
  submission: HtsSubmission | null;
  latestSubmittedId: string | null;
  // Server-decided — which Disclosure & Consent variant to show.
  isGuest: boolean;
}

/** One asked/answered follow-up round. `answer: null` means asked but not yet answered — at most one such entry, always last. */
export interface HtsFollowUpExchange {
  question: string;
  answer: string | null;
}

export interface HtsAnswer {
  id: string;
  submissionId: string;
  questionId: string;
  value: string | string[];
  updatedAt: string;
  // True once this answer has used its one-time redo — a further change is
  // rejected by the server (HtsAnswerRedoExhaustedError).
  redoUsed: boolean;
  // The full history of AI follow-up exchanges for this answer, oldest
  // first — never classification/flag data, just question text and the
  // candidate's own past answers (see the router's sanitizeSubmissionForCandidate).
  followUpHistory?: HtsFollowUpExchange[];
}

export interface HtsAdminFollowUpItem {
  questionId: string;
  /** 1-based position in the questionnaire. */
  questionNumber: number;
  questionText: string;
  answerValue: string | string[];
  groupFields: HtsGroupField[] | null;
  followUpHistory: HtsFollowUpExchange[];
  followUpQuestion: string;
}

export interface HtsAdminFollowUps {
  status: HtsAdminFollowUpStatus;
  items: HtsAdminFollowUpItem[];
}

export interface HtsSubmission {
  id: string;
  status: HtsSubmissionStatus;
  userId: number;
  interviewTemplateId: string;
  submittedAt: string | null;
  consentedAt: string | null;
  consentVersion: string | null;
  answers: HtsAnswer[];
  // Null until the fixed perfect-person question has been answered. Lets
  // the page rehydrate the rail entry/modal count after a reload without a
  // separate round trip.
  perfectPersonProbe: { question: string; answer: string } | null;
}

export interface HtsAnswerInput {
  questionId: string;
  value: string | string[];
}

/** The candidate's answer to the most recently pending AI-generated follow-up question for one question. */
export interface HtsFollowUpAnswerInput {
  questionId: string;
  answer: string;
}

/** Never carries classification/flag data — only the follow-up question text and the candidate's own past answers. */
export interface HtsFollowUpOutcome {
  followUpQuestion: string | null;
  resolved: boolean;
  followUpRound: number;
  history: HtsFollowUpExchange[];
}

export interface HtsSaveAnswersResult extends HtsSubmission {
  followUps: Record<string, HtsFollowUpOutcome>;
}

/** Submit is blocked pending this fixed question until it's answered via `answerPerfectPersonProbe`. */
export interface HtsPendingPerfectPersonProbe {
  pending: true;
  perfectPersonQuestion: string;
}

export type HtsSubmitResult = HtsSubmission | HtsPendingPerfectPersonProbe;

export const isHtsPendingPerfectPersonProbe = (
  result: HtsSubmitResult
): result is HtsPendingPerfectPersonProbe => "pending" in result && result.pending === true;

/** Read-only status from `checkPerfectPersonProbe` — never finalizes the submission. */
export interface HtsPerfectPersonProbeStatus {
  pending: boolean;
  perfectPersonQuestion?: string;
  // Present only when it was already answered previously (`pending: false`
  // because it's done, not because it never applied).
  perfectPersonAnswer?: string;
}

// =============================================================================
// SERVICE
// =============================================================================

export class HtsService extends BaseAPIClient {
  /** Get the HTS questions attached to a purchased template. */
  async getQuestionsForTemplate(templateId: string): Promise<HtsQuestionsForTemplate> {
    return this.get<HtsQuestionsForTemplate>(`/hts/questions/${encodeURIComponent(templateId)}`);
  }

  /** Find the current user's existing submission for this template, or start a new draft. */
  async startOrResumeSubmission(interviewTemplateId: string): Promise<HtsSubmission> {
    return this.post<HtsSubmission>('/hts/submissions', { interviewTemplateId });
  }

  /**
   * Save/update answers on a draft submission as the candidate progresses.
   * `followUpAnswer` answers the most recently pending AI-generated
   * follow-up question for one of the questions in `answers`, if any.
   */
  async saveAnswers(
    submissionId: string,
    answers: HtsAnswerInput[],
    followUpAnswer?: HtsFollowUpAnswerInput
  ): Promise<HtsSaveAnswersResult> {
    return this.patch<HtsSaveAnswersResult>(`/hts/submissions/${submissionId}/answers`, {
      answers,
      followUpAnswer,
    });
  }

  /** Finalize a draft submission — may instead return a pending perfect-person question. */
  async submit(submissionId: string): Promise<HtsSubmitResult> {
    return this.patch<HtsSubmitResult>(`/hts/submissions/${submissionId}/submit`);
  }

  /**
   * Read-only check for whether the fixed perfect-person question is
   * pending — never finalizes the submission. Used to surface it as one
   * more question at the end of the flow, before the confirm-submit modal.
   */
  async checkPerfectPersonProbe(submissionId: string): Promise<HtsPerfectPersonProbeStatus> {
    return this.get<HtsPerfectPersonProbeStatus>(`/hts/submissions/${submissionId}/perfect-person-probe`);
  }

  /** Answers the fixed perfect-person question. Call `submit` afterward to finalize. */
  async answerPerfectPersonProbe(submissionId: string, answer: string): Promise<{ saved: boolean }> {
    return this.patch<{ saved: boolean }>(`/hts/submissions/${submissionId}/perfect-person-probe`, { answer });
  }

  /** The candidate-safe, numbers-free Readiness Profile summary — available once the submission has been scored. */
  async getReadinessSummary(submissionId: string): Promise<HtsReadinessSummary> {
    return this.get<HtsReadinessSummary>(`/hts/submissions/${submissionId}/readiness`);
  }

  /** The admin's follow-up questions for this submission — 404 if none were sent. */
  async getAdminFollowUps(submissionId: string): Promise<HtsAdminFollowUps> {
    return this.get<HtsAdminFollowUps>(`/hts/submissions/${submissionId}/admin-follow-ups`);
  }

  /** Answers every follow-up at once and closes the round. */
  async submitAdminFollowUps(
    submissionId: string,
    answers: { questionId: string; answer: string }[]
  ): Promise<{ saved: boolean }> {
    return this.patch<{ saved: boolean }>(`/hts/submissions/${submissionId}/admin-follow-ups`, { answers });
  }

  /** The current user's own submitted HTS submissions — for the session-history list. */
  async listMySubmissions(page: number, limit: number): Promise<PaginatedHtsSubmissionsForUser> {
    return this.get<PaginatedHtsSubmissionsForUser>(`/hts/submissions?page=${page}&limit=${limit}`);
  }
}

export default HtsService;
